/**
 * The dockable workstation layout.
 *
 *   title bar
 *   menu bar
 *   collapsible ribbon
 *   ┌ left panel │ centre │ right panel ┐
 *   └ bottom dock                       ┘
 *   status bar
 *
 * The shell owns geometry only. Panel *contents* come from the tab registry, so
 * a workstream ships a tab by calling `registerTab` — no edit here.
 *
 * Scrolling rule: this component is `overflow-hidden` from top to bottom and
 * every region is `min-h-0`/`min-w-0`, so the page itself can never scroll;
 * only panel bodies do.
 */

import * as React from "react";
import { PanelLeft, PanelRight } from "lucide-react";

import { EmptyState, Panel } from "@/components/ui/primitives";
import { RIBBON_TABS } from "@/ribbon/ribbon.config";
import { hasActionHandler, registerAction } from "@/shell/actions";
import { Dock, DockRail } from "@/shell/Dock";
import { ErrorBoundary } from "@/shell/ErrorBoundary";
import { CommandPalette } from "@/shell/CommandPalette";
import { HelpOverlays } from "@/shell/HelpOverlays";
import { MenuBar } from "@/shell/MenuBar";
import { Ribbon } from "@/shell/Ribbon";
import { Splitter } from "@/shell/Splitter";
import { StatusBar } from "@/shell/StatusBar";
import { TitleBar } from "@/shell/TitleBar";
import { ToolLaunchDialog } from "@/tools/ToolLaunchDialog";
import { InteractiveSegPanel } from "@/tools/segment/InteractiveSegPanel";
import { StageRunPanel } from "@/tools/stageRun/StageRunPanel";
import { LaunchSheet } from "@/nav/LaunchSheet";
import { RunBar } from "@/pipeline/RunBar";
import { useTabModule, type DockTabId, type TabId } from "@/shell/tabRegistry";
import { toggleFullScreen, closeWindow } from "@/shell/window";
import { useActiveTab, useAppStore, usePanels, useRibbonMode } from "@/store/appStore";

/**
 * Ribbon toggle ids the shell mirrors from panel state. This is the one place
 * the shell knows a config id; the coupling is one-directional (panels drive
 * the toggle, never the reverse) so a rename here fails visibly, not silently.
 */
const PANEL_TOGGLE_ID = {
  left: "view.panel.studyExplorer",
  right: "view.panel.inspector",
} as const;

function tabLabel(id: TabId): string {
  return RIBBON_TABS.find((t) => t.id === id)?.label ?? id;
}

function Placeholder({ tab, slot }: { tab: TabId; slot: "centre" | "left" | "right" }) {
  if (slot === "centre") {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          title={`${tabLabel(tab)} tab — under construction`}
          hint="This view is owned by another workstream and will appear here once it lands."
        />
      </div>
    );
  }
  return (
    <Panel title={slot === "left" ? "Explorer" : "Properties"} className="h-full">
      <EmptyState title="Empty" hint={`No ${slot} panel registered for ${tabLabel(tab)}.`} />
    </Panel>
  );
}

/** Collapsed side panel: a thin rail that clicks back open. */
function SideRail({ side, onExpand }: { side: "left" | "right"; onExpand: () => void }) {
  const Icon = side === "left" ? PanelLeft : PanelRight;
  return (
    <button
      type="button"
      title={`Show ${side} panel`}
      onClick={onExpand}
      className="flex w-6 shrink-0 items-start justify-center border-ads-border bg-ads-surface pt-2 text-ads-muted hover:bg-ads-hover"
      style={{ borderRightWidth: side === "left" ? 1 : 0, borderLeftWidth: side === "right" ? 1 : 0 }}
    >
      <Icon size={14} />
    </button>
  );
}

export function AppShell() {
  const activeTab = useActiveTab();
  const mod = useTabModule(activeTab);
  const panels = usePanels();
  const setPanelSize = useAppStore((s) => s.setPanelSize);
  const togglePanel = useAppStore((s) => s.togglePanel);
  const setToggle = useAppStore((s) => s.setToggle);
  const setDockTab = useAppStore((s) => s.setDockTab);

  // Shell-owned actions. Feature workstreams register theirs the same way; a
  // name may have several handlers, so these never block a workstream's own.
  React.useEffect(() => {
    const offs = [
      registerAction("panel.toggle.left", () => togglePanel("left")),
      registerAction("panel.toggle.right", () => togglePanel("right")),
      registerAction("panel.toggle.bottom", () => togglePanel("bottom")),
      registerAction("window.fullscreen", () => void toggleFullScreen()),
      // Reachable by name, for anyone who hid the toolbar and went looking in
      // the command palette rather than at the strip it left behind.
      registerAction("ribbon.show", () => useAppStore.getState().setRibbonMode("pinned")),
      registerAction("ribbon.hide", () => useAppStore.getState().setRibbonMode("collapsed")),
      registerAction("ribbon.toggle", () => {
        const mode = useAppStore.getState().ribbonMode;
        useAppStore.getState().setRibbonMode(mode === "pinned" ? "collapsed" : "pinned");
      }),
      // Fallback only: workstream F owns "file.exit" when its module is loaded.
      ...(hasActionHandler("file.exit") ? [] : [registerAction("file.exit", () => void closeWindow())]),
      ...(["jobs", "log", "qc", "console"] as DockTabId[]).map((id) =>
        registerAction(`dock.show.${id}`, () => {
          setDockTab(id);
          togglePanel("bottom", false);
        }),
      ),
    ];
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "F1") {
        event.preventDefault();
        const mode = useAppStore.getState().ribbonMode;
        useAppStore.getState().setRibbonMode(mode === "pinned" ? "collapsed" : "pinned");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      offs.forEach((off) => off());
      window.removeEventListener("keydown", onKey);
    };
  }, [togglePanel, setDockTab]);

  // Keep the ribbon's panel toggles honest when a rail or dock button changes
  // panel state behind the ribbon's back.
  React.useEffect(() => {
    setToggle(PANEL_TOGGLE_ID.left, !panels.leftCollapsed);
    setToggle(PANEL_TOGGLE_ID.right, !panels.rightCollapsed);
  }, [panels.leftCollapsed, panels.rightCollapsed, setToggle]);

  const Centre = mod?.centre;
  const Left = mod?.left;
  const Right = mod?.right;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-ads-bg text-ads-text">
      <TitleBar />
      <ShellChrome />
      <CommandPalette />
      <HelpOverlays />
      <ToolLaunchDialog />
      <InteractiveSegPanel />
      <StageRunPanel />
      <LaunchSheet />

      <div className="flex min-h-0 flex-1 flex-col">
        {/* Above the workspace, not inside a tab: a run is visible from
            wherever you are standing. */}
        <RunBar />
        <div className="flex min-h-0 flex-1">
          {!Left ? null : panels.leftCollapsed ? (
            <SideRail side="left" onExpand={() => togglePanel("left", false)} />
          ) : (
            <>
              <aside
                className="flex min-h-0 shrink-0 flex-col overflow-hidden bg-ads-surface"
                style={{ width: panels.leftWidth }}
              >
                <ErrorBoundary label={`${tabLabel(activeTab)} left panel`}>
                  {Left ? <Left /> : <Placeholder tab={activeTab} slot="left" />}
                </ErrorBoundary>
              </aside>
              <Splitter
                orientation="vertical"
                edge="start"
                size={panels.leftWidth}
                min={180}
                max={520}
                onResize={(px) => setPanelSize("leftWidth", px)}
                onDoubleClick={() => togglePanel("left", true)}
              />
            </>
          )}

          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-ads-bg">
            <ErrorBoundary key={activeTab} label={`${tabLabel(activeTab)} tab`}>
              {Centre ? <Centre /> : <Placeholder tab={activeTab} slot="centre" />}
            </ErrorBoundary>
          </main>

          {!Right ? null : panels.rightCollapsed ? (
            <SideRail side="right" onExpand={() => togglePanel("right", false)} />
          ) : (
            <>
              <Splitter
                orientation="vertical"
                edge="end"
                size={panels.rightWidth}
                min={220}
                max={560}
                onResize={(px) => setPanelSize("rightWidth", px)}
                onDoubleClick={() => togglePanel("right", true)}
              />
              <aside
                className="flex min-h-0 shrink-0 flex-col overflow-hidden bg-ads-surface"
                style={{ width: panels.rightWidth }}
              >
                <ErrorBoundary label={`${tabLabel(activeTab)} right panel`}>
                  {Right ? <Right /> : <Placeholder tab={activeTab} slot="right" />}
                </ErrorBoundary>
              </aside>
            </>
          )}
        </div>

        {panels.bottomCollapsed ? (
          <DockRail />
        ) : (
          <>
            <Splitter
              orientation="horizontal"
              edge="end"
              size={panels.bottomHeight}
              min={90}
              max={520}
              onResize={(px) => setPanelSize("bottomHeight", px)}
              onDoubleClick={() => togglePanel("bottom", true)}
            />
            <div
              className="flex min-h-0 shrink-0 flex-col overflow-hidden border-t border-ads-border"
              style={{ height: panels.bottomHeight }}
            >
              <ErrorBoundary label="Dock">
                <Dock />
              </ErrorBoundary>
            </div>
          </>
        )}
      </div>

      <StatusBar />
    </div>
  );
}

/**
 * The command band above the workspace bar.
 *
 * There is no ribbon here any more. Nothing in this domain uses one — not
 * syngo.via, IntelliSpace, AW, Vitrea, TeraRecon, OHIF, Horos, Weasis,
 * RadiAnt, Slicer, ITK-SNAP or MITK — and the ribbon's own specification says
 * it "can replace both the traditional menu bar and toolbars", so running one
 * alongside a menu bar *and* a workspace bar paid its full height cost three
 * times over, in the one kind of application where the content area matters
 * most. It also carried 168 labelled items in 33 groups, with 8 action ids
 * and 17 labels appearing in more than one place.
 *
 * What replaced it: outcomes on Start, the viewer's own toolbar for display
 * controls (next to the pixels, where the eye already is), and the command
 * palette for everything by name. The menu bar stays for research mode, where
 * reaching a specific operation directly is the point.
 */
function ShellChrome() {
  // Menu bar and toolbar both stay. They were never the problem; the rule
  // between them was.
  //
  // The rule now, with no exceptions: **the workspace bar is the only thing
  // that changes where you are.** A menu name opens its menu, every time.
  // Selecting an item runs a command, and if that command happens to land you
  // in the viewer, that is the command's doing, not the menu's.
  //
  // What made it feel arbitrary was two rows carrying the same four names —
  // Home, Pipeline, View, Results were in the menu bar *and* the workspace
  // bar — plus Quantification, which switched workspace while its eleven
  // neighbours opened a menu. Both are fixed in MenuBar: the places are gone
  // from it, and nothing is special-cased.
  //
  // The toolbar needs no help from the menu bar to know what to show:
  // `Ribbon` reads `useActiveTab()` and renders the band for wherever you
  // are. Pin / collapse / auto-hide sit on the band, and Ctrl/Cmd+F1 toggles
  // it.
  const mode = useRibbonMode();
  const [peek, setPeek] = React.useState(false);
  const hideTimer = React.useRef<number>(0);

  const cancelHide = () => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  };

  const scheduleHide = () => {
    cancelHide();
    if (mode === "pinned") return;
    hideTimer.current = window.setTimeout(() => setPeek(false), mode === "autohide" ? 350 : 700);
  };

  React.useEffect(() => () => cancelHide(), []);

  React.useEffect(() => {
    if (mode === "pinned") setPeek(false);
  }, [mode]);

  const visible = mode === "pinned" || peek;

  return (
    <div
      className="shrink-0"
      onMouseEnter={() => {
        cancelHide();
        if (mode === "autohide") setPeek(true);
      }}
      onMouseLeave={scheduleHide}
    >
      <MenuBar />
      <Ribbon visible={visible} />
    </div>
  );
}



